import { jsx, jsxs } from "react/jsx-runtime";
import { bool, string, object, arrayOf, oneOf, oneOfType, node, func, element } from "prop-types";
import React from "react";
import cx from "classnames";
import Image from "@ingka/image";
import AspectRatioBox from "@ingka/aspect-ratio-box";
import PaymentLogo from "@ingka/payment-logo";
import AnimateHeight from "./AnimateHeight";
import SSRIcon from "@ingka/ssr-icon";
const propTypes = {
  /** Unique string id used to handle selection [Required] */
  id: string.isRequired,
  /** Can be used to add classes to base HTML element. */
  className: string,
  /** Function returning react element with paths for SSRIcon from `@ingka/ssr-icon/paths/[icon].js` */
  ssrIcon: func,
  /** ChoiceItem custom content */
  customContent: node,
  /** Optional nested content */
  children: oneOfType([
    /** Array of elements used as children. */
    arrayOf(node),
    /** One or more elements without array. */
    node
  ]),
  /** ChoiceItem disabled state */
  disabled: bool,
  /** ChoiceItem selection style, should be used only when used as a standalone component */
  subtle: bool,
  /** Determines the size of the ChoiceItems */
  size: oneOf(["small", "medium", "large"]),
  /** Determines the horizontal alignment of the image (image, icon & payment-logo) for ChoiceItem*/
  imageAlignment: oneOf(["top", "center"]),
  /** Determines the horizontal alignment of the trailing value for ChoiceItem*/
  valueAlignment: oneOf(["top", "center"]),
  /** Determines if ChoiceItem action will be 'anchor' or 'button' */
  href: string,
  /** `@ingka/image` props */
  image: object,
  /** ChoiceItem image layout, one of ['leading', 'trailing'] */
  imagePosition: string,
  /** PaymentLogo */
  paymentLogo: object,
  /** Callback invoked after selection. */
  onClick: func,
  /** ChoiceItem selected state */
  selected: bool,
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** ChoiceItem caption */
  caption: oneOfType([string, element]),
  /** ChoiceItem title */
  title: oneOfType([string, element]),
  /** ChoiceItem value */
  value: oneOfType([string, element]),
  controlProps: object
};
const ChoiceItem = (props) => {
  const {
    id,
    className,
    ssrIcon,
    customContent,
    children,
    disabled,
    subtle,
    size,
    imageAlignment = "center",
    valueAlignment = "top",
    href,
    image,
    imagePosition = "leading",
    paymentLogo,
    onClick = () => {
    },
    selected,
    prefix = "",
    caption,
    title,
    value,
    controlProps,
    ...other
  } = props;
  const baseClass = `${prefix}choice-item`;
  const classes = cx(
    baseClass,
    selected && `${baseClass}--selected`,
    subtle && `${baseClass}--subtle`,
    imagePosition === "trailing" && `${baseClass}--trailing-image`,
    className
  );
  const ChoiceAction = href ? "a" : "button";
  const choiceContent = /* @__PURE__ */ jsxs("span", { className: `${baseClass}__content`, children: [
    /* @__PURE__ */ jsxs("span", { className: `${baseClass}__text`, children: [
      /* @__PURE__ */ jsx("span", { className: `${baseClass}__title`, children: title }),
      caption && /* @__PURE__ */ jsx("span", { className: `${baseClass}__caption`, children: caption })
    ] }),
    imagePosition !== "trailing" && /* @__PURE__ */ jsx("span", { className: `${baseClass}__values`, children: value && /* @__PURE__ */ jsx(
      "span",
      {
        className: valueAlignment === "center" && caption ? `${baseClass}__value ${baseClass}__value--center` : `${baseClass}__value`,
        children: value
      }
    ) })
  ] });
  return /* @__PURE__ */ jsxs("div", { ...other, id, className: classes, children: [
    /* @__PURE__ */ jsxs(
      ChoiceAction,
      {
        href,
        type: !href && "button",
        rel: href && "noopener noreferrer",
        "aria-pressed": !href && selected,
        className: imageAlignment === "top" && caption ? `${baseClass}__action ${baseClass}__action--top` : `${baseClass}__action`,
        disabled,
        onClick: () => onClick(id),
        ...controlProps,
        children: [
          ssrIcon && !image && !paymentLogo && /* @__PURE__ */ jsx(
            SSRIcon,
            {
              prefix,
              className: `${baseClass}__icon`,
              paths: ssrIcon
            }
          ),
          paymentLogo && !image && /* @__PURE__ */ jsx(
            PaymentLogo,
            {
              size: size === "small" ? "small" : "medium",
              src: paymentLogo.src,
              alt: paymentLogo.alt,
              prefix,
              className: `${baseClass}__payment-logo`
            }
          ),
          image && /* @__PURE__ */ jsx("span", { className: `${baseClass}__image`, children: /* @__PURE__ */ jsx(AspectRatioBox, { ratio: "square", prefix, children: /* @__PURE__ */ jsx(Image, { prefix, ...image }) }) }),
          customContent ? /* @__PURE__ */ jsx("span", { className: `${baseClass}__custom-content`, children: customContent }) : choiceContent
        ]
      }
    ),
    /* @__PURE__ */ jsx("span", { className: `${baseClass}__border`, "aria-hidden": "true" }),
    children && /* @__PURE__ */ jsx(
      AnimateHeight,
      {
        animateOpacity: true,
        expanded: children && selected && !disabled,
        children: /* @__PURE__ */ jsx("div", { className: `${baseClass}__nested`, children })
      }
    )
  ] });
};
ChoiceItem.propTypes = propTypes;
var ChoiceItem_default = ChoiceItem;
export {
  ChoiceItem_default as default
};
